import { useEffect, useState } from 'react';
import { createContainer } from 'unstated-next';
import useAppState from '../../app/use-app-state';
import { useConfigControllerFindAll } from '../../v2/api/generated/configs/configs';
import {
  CONFIG_TYPE_LANGUAGE,
  DEFAULT_LANGUAGE_CONFIG_KEY,
  getLocaleKey,
} from '../../v2/pages/organization/configurations/language/constants';
import type {
  Config,
  ConfigApiResponse,
} from '../../v2/pages/organization/configurations/language/types';
import AuthContainer from '../../v2/shared/containers/AuthContainer';
import { getLoginLocale } from '../../v2/auth/AdminLoginContainer';

const PatientOrgContainer = createContainer(() => {
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('en');

  // Get authentication state
  const { isLogin } = AuthContainer.useContainer();

  // Get current locale and setLocale function from app state
  const [
    {
      appStateCookie: { localeKey: currentLocale },
    },
    { setLocale },
  ] = useAppState();

  // Fetch organization config to get default language
  const { data: configData, isError: isConfigError } = useConfigControllerFindAll({
    query: {
      enabled: !!organizationId && isLogin,
    },
  });

  // Apply organization language from config
  useEffect(() => {
    if (!organizationId) return;

    // If API error occurred, fallback to browser language
    if (isConfigError) {
      console.warn('Failed to fetch organization config, falling back to browser language');
      const browserLocaleKey = getLocaleKey(getLoginLocale());
      if (currentLocale !== browserLocaleKey) {
        setLocale(browserLocaleKey);
      }
      return;
    }

    if (!configData) return;

    // Handle nested API response structure
    const response = configData as ConfigApiResponse;
    let configList: Config[] = [];

    if (response?.data?.data) {
      configList = response.data.data;
    } else if (response?.data && Array.isArray(response.data)) {
      configList = response.data as unknown as Config[];
    } else if (Array.isArray(configData)) {
      configList = configData as unknown as Config[];
    }

    const languageConfig = configList.find(
      (config: Config) =>
        config.configType === CONFIG_TYPE_LANGUAGE &&
        config.key === DEFAULT_LANGUAGE_CONFIG_KEY &&
        config.organizationId === organizationId,
    );

    if (languageConfig?.value) {
      // Use getLocaleKey for consistent locale normalization
      const targetLocaleKey = getLocaleKey(languageConfig.value);
      setDefaultLanguage(languageConfig.value);

      // Priority 1: Org language exists → set it
      if (currentLocale !== targetLocaleKey) {
        setLocale(targetLocaleKey);
      }
    } else {
      // Priority 2: Org language not in DB → use browser language
      const browserLocaleKey = getLocaleKey(getLoginLocale());
      if (currentLocale !== browserLocaleKey) {
        setLocale(browserLocaleKey);
      }
    }
  }, [configData, organizationId, currentLocale, setLocale, isConfigError]);

  return {
    organizationId,
    orgName,
    logoUrl,
    isLoading,
    defaultLanguage,
    setOrganizationId,
    setOrgName,
    setLogoUrl,
    setIsLoading,
  };
});

export default PatientOrgContainer;
